/**
 * Opens the "What's new" dialog at desktop start when an entry was published
 * after the person last closed it (#1355). Decided once per session, after
 * the settings loaded — a failed settings load or changelog request shows
 * nothing and writes nothing.
 */

import { useEffect, useRef, useState } from 'react'
import { useSettingsContext } from '@/contexts/SettingsContext'
import { useChangelog } from '@/hooks/useChangelog'
import { isNewerThan, startupAction, type StartupAction } from '@/lib/changelog'

export function useChangelogStartup(onShow: () => void) {
  const { settings, hasLoaded, saveSettings } = useSettingsContext()
  const [action, setAction] = useState<StartupAction | null>(null)
  const lastSeenAtRef = useRef<string | undefined>(undefined)
  const decidedRef = useRef(false)
  const comparedRef = useRef(false)

  useEffect(() => {
    if (!hasLoaded || decidedRef.current) return
    decidedRef.current = true
    const next = startupAction(settings.changelog)
    lastSeenAtRef.current = settings.changelog?.lastSeenAt
    setAction(next)
    if (next === 'initialize') {
      void saveSettings({
        ...settings,
        changelog: { ...settings.changelog, lastSeenAt: new Date().toISOString() },
      })
    }
  }, [hasLoaded, settings, saveSettings])

  const { entries, isSuccess } = useChangelog(action === 'check')

  useEffect(() => {
    if (action !== 'check' || !isSuccess || comparedRef.current) return
    comparedRef.current = true
    const newest = entries[0]
    const lastSeenAt = lastSeenAtRef.current
    if (newest && lastSeenAt && isNewerThan(newest.publishedAt, lastSeenAt)) onShow()
  }, [action, isSuccess, entries, onShow])
}
